import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDateTime, formatGel } from '../lib/format';
import { useCodeLabel, useI18n } from '../lib/i18n';
import { useAudit } from '../lib/queries';
import { useDebounced } from '../lib/useDebounced';
import { Card, EmptyState, Pill, Skeleton } from '../components/ui';

const FILTERS = [
  { label: 'audit.filterAll', value: '' },
  { label: 'audit.filterOrders', value: 'order.' },
  { label: 'audit.filterCustomers', value: 'customer.' },
  { label: 'audit.filterLoyalty', value: 'loyalty.' },
  { label: 'audit.filterBookings', value: 'booking.' },
  { label: 'audit.filterSettings', value: 'settings.' },
  { label: 'audit.filterSignIns', value: 'admin.login' },
] as const;

/** Money keys are rendered as money; everything else is left as it was recorded. */
const MONEY_KEYS = new Set([
  'orderValueTetri', 'cashbackUsedTetri', 'cashbackEarnedTetri', 'refundValueTetri',
  'cashRefundTetri', 'cashbackReturnedTetri', 'cashbackReversedTetri', 'cashbackForgivenTetri',
]);

function renderValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (MONEY_KEYS.has(key) && typeof value === 'number') return formatGel(value);
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function AuditPage() {
  const { t } = useI18n();
  const code = useCodeLabel();
  const [filter, setFilter] = useState('');
  const debounced = useDebounced(filter, 150);
  const { data, isPending } = useAudit(debounced);

  const items = data?.items ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <header className="mb-5">
        <h1 className="font-display text-[1.75rem] font-bold leading-tight tracking-tight text-ink sm:text-[2rem]">{t('audit.title')}</h1>
      </header>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {FILTERS.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            aria-pressed={filter === option.value}
            className={`inline-flex min-h-10 items-center rounded-lg border px-3 text-xs font-medium transition-colors sm:min-h-8 sm:px-2.5 ${
              filter === option.value
                ? 'border-brand bg-brand-wash text-brand'
                : 'border-line bg-raised text-ink-dim hover:text-ink'
            }`}
          >
            {t(option.label)}
          </button>
        ))}
      </div>

      {isPending && (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20" />)}</div>
      )}

      {!isPending && items.length === 0 && <EmptyState title={t('audit.empty')} />}

      <div className="space-y-2">
        {items.map((entry) => {
          const details = Object.entries(entry.metadata ?? {}).filter(
            ([, value]) => value !== null && value !== undefined && value !== '',
          );
          return (
            <Card key={entry.id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-semibold text-ink">
                      {code('auditAction', entry.action)}
                    </span>
                    <Pill tone={entry.actorType === 'SYSTEM' ? 'warning' : 'neutral'}>
                      {code('actor', entry.actorType)}
                    </Pill>
                  </div>
                  <div className="mt-1 text-xs text-ink-faint">
                    {entry.actorLabel ?? t('common.system')}
                    {entry.ipAddress && <span className="tabular ml-2 opacity-70">{entry.ipAddress}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="tabular text-[0.7rem] text-ink-faint">
                    {formatDateTime(entry.createdAt)}
                  </div>
                  <div className="tabular text-[0.62rem] text-ink-faint/70">{entry.action}</div>
                  {entry.entityType === 'customer' && entry.entityId && (
                    <Link
                      to={`/customers/${entry.entityId}`}
                      className="-my-1.5 inline-flex min-h-9 items-center text-[0.7rem] text-brand hover:underline"
                    >
                      {t('audit.openCustomer')}
                    </Link>
                  )}
                  {entry.entityType === 'order' && entry.entityId && (
                    <Link
                      to={`/orders/${entry.entityId}`}
                      className="-my-1.5 inline-flex min-h-9 items-center text-[0.7rem] text-brand hover:underline"
                    >
                      {t('audit.openOrder')}
                    </Link>
                  )}
                </div>
              </div>

              {details.length > 0 && (
                <dl className="mt-3 grid gap-x-4 gap-y-1 border-t border-line pt-2.5 text-[0.7rem] sm:grid-cols-2">
                  {details.map(([key, value]) => (
                    <div key={key} className="flex min-w-0 justify-between gap-3">
                      <dt className="shrink-0 text-ink-faint">{key}</dt>
                      {/*
                        Wrapped, not truncated: half of these values are ids
                        somebody is reading off the screen to find a session,
                        and a flex child needs min-w-0 or it refuses to shrink
                        at all — which pushed the whole page sideways.
                      */}
                      <dd className="tabular min-w-0 break-all text-right text-ink-dim">
                        {renderValue(key, value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
